import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { Obra, ObraInput } from "../types/Obra";
import LocationPickerMap from "../../customerDashboard/components/LocationPicker.js";
import { Camara } from "../../customerDashboard/types/Camara";
import { User } from "../../customerDashboard/types/User";

const OBELISCO_COORDS = { lat: -34.603722, lng: -58.381592 };

export interface ObraAssignments {
  cameraIds: string[];
  userIds: string[];
}

interface ObraFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  obraData?: Obra;
  camaras: Camara[];
  users: User[];
  onConfirm: (data: ObraInput, assignments: ObraAssignments) => void;
  isSubmitting?: boolean;
}

const defaultForm: ObraInput = {
  nombre: "",
  active: true,
  direccion: "",
  ubicacionMaps: null,
};

const ObraFormModal = ({ isOpen, onClose, obraData, camaras, users, onConfirm, isSubmitting = false }: ObraFormModalProps) => {
  const [form, setForm] = useState<ObraInput>(defaultForm);
  const [hasUbicacion, setHasUbicacion] = useState(false);
  const [selectedCameraIds, setSelectedCameraIds] = useState<string[]>([]);
  const [selectedUserIds, setSelectedUserIds] = useState<string[]>([]);
  const isNew = !obraData;

  useEffect(() => {
    if (!isOpen) return;
    if (obraData) {
      setForm({
        nombre: obraData.nombre,
        active: obraData.active,
        direccion: obraData.direccion || "",
        ubicacionMaps: obraData.ubicacionMaps,
      });
      setHasUbicacion(!!obraData.ubicacionMaps);
      setSelectedCameraIds(camaras.filter((c) => c.obra === obraData.id).map((c) => c.id!));
      setSelectedUserIds(users.filter((u) => (u.obra || []).includes(obraData.id)).map((u) => u.id!));
    } else {
      setForm(defaultForm);
      setHasUbicacion(false);
      setSelectedCameraIds([]);
      setSelectedUserIds([]);
    }
  }, [isOpen, obraData, camaras, users]);

  const toggleId = (id: string, setter: React.Dispatch<React.SetStateAction<string[]>>) => {
    setter((prev) => (prev.includes(id) ? prev.filter((v) => v !== id) : [...prev, id]));
  };

  const handleSubmit = () => {
    if (isSubmitting) return;
    if (!form.nombre.trim()) {
      toast.error("El nombre de la obra es obligatorio.");
      return;
    }

    const dataToSubmit: ObraInput = {
      ...form,
      nombre: form.nombre.trim(),
      direccion: form.direccion?.trim() ? form.direccion.trim() : null,
      ubicacionMaps: hasUbicacion ? form.ubicacionMaps || OBELISCO_COORDS : null,
    };

    onConfirm(dataToSubmit, { cameraIds: selectedCameraIds, userIds: selectedUserIds });
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-start justify-center p-4 z-[70] overflow-y-auto"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="relative bg-white rounded-xl w-full max-w-lg mt-16 mb-8 shadow-lg flex flex-col">
        <div className="flex justify-between items-center border-b p-4 md:p-6">
          <h2 className="text-xl font-bold">{isNew ? "🏗️ Crear obra" : "✏️ Editar obra"}</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-2xl">✕</button>
        </div>

        <div className="p-4 md:p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">Nombre*</label>
            <input
              type="text"
              value={form.nombre}
              onChange={(e) => setForm((prev) => ({ ...prev, nombre: e.target.value }))}
              className="w-full border rounded-lg px-3 py-2"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">Dirección. Ej. Agustín Méndez 80, C1426 Cdad. Autónoma de Buenos Aires</label>
            <input
              type="text"
              value={form.direccion || ""}
              onChange={(e) => setForm((prev) => ({ ...prev, direccion: e.target.value }))}
              className="w-full border rounded-lg px-3 py-2"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">Estado</label>
            <select
              value={form.active ? "true" : "false"}
              onChange={(e) => setForm((prev) => ({ ...prev, active: e.target.value === "true" }))}
              className="w-full border rounded-lg px-3 py-2 bg-white"
            >
              <option value="true">Activa</option>
              <option value="false">Inactiva</option>
            </select>
          </div>

          <div className="border rounded-lg p-3 bg-gray-50">
            <label className="flex items-center text-sm font-medium text-gray-700 mb-2 cursor-pointer">
              <input
                type="checkbox"
                checked={hasUbicacion}
                onChange={(e) => {
                  const checked = e.target.checked;
                  setHasUbicacion(checked);
                  setForm((prev) => ({
                    ...prev,
                    ubicacionMaps: checked ? prev.ubicacionMaps || OBELISCO_COORDS : null,
                  }));
                }}
                className="mr-2 h-4 w-4 text-blue-600"
              />
              Ubicación en mapa
            </label>
            {hasUbicacion && (
              <div className="h-[400px] w-full border border-gray-300 rounded-lg overflow-hidden">
                <LocationPickerMap
                  initialCoordinates={form.ubicacionMaps || OBELISCO_COORDS}
                  onCoordinatesChange={(coords: { lat: number; lng: number }) =>
                    setForm((prev) => ({ ...prev, ubicacionMaps: coords }))
                  }
                />
              </div>
            )}
          </div>

          {camaras.length > 0 && (
            <div className="border rounded-lg p-3 bg-green-50">
              <h4 className="text-sm font-medium text-gray-700 mb-2">Cámaras asignadas</h4>
              <div className="space-y-2 max-h-40 overflow-y-auto">
                {camaras.map((c) => (
                  <label key={c.id} className="flex items-center text-sm cursor-pointer">
                    <input
                      type="checkbox"
                      checked={selectedCameraIds.includes(c.id!)}
                      onChange={() => toggleId(c.id!, setSelectedCameraIds)}
                      className="mr-2 h-4 w-4 text-blue-600"
                    />
                    {c.Name}
                  </label>
                ))}
              </div>
            </div>
          )}

          {users.length > 0 && (
            <div className="border rounded-lg p-3 bg-purple-50">
              <h4 className="text-sm font-medium text-gray-700 mb-2">Usuarios asignados</h4>
              <div className="space-y-2 max-h-40 overflow-y-auto">
                {users.map((u) => (
                  <label key={u.id} className="flex items-center text-sm cursor-pointer">
                    <input
                      type="checkbox"
                      checked={selectedUserIds.includes(u.id!)}
                      onChange={() => toggleId(u.id!, setSelectedUserIds)}
                      className="mr-2 h-4 w-4 text-blue-600"
                    />
                    {u.name} {u.surname}
                  </label>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="flex justify-end gap-3 border-t p-4 bg-gray-50 rounded-b-xl">
          <button onClick={onClose} disabled={isSubmitting} className="px-6 py-2 bg-gray-300 rounded-lg font-medium disabled:opacity-50">Cancelar</button>
          <button
            onClick={handleSubmit}
            disabled={isSubmitting}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg font-medium disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSubmitting ? "Guardando..." : isNew ? "Crear obra" : "Actualizar obra"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ObraFormModal;
